/* Dust & Mile product page: variant picker, add to cart, gallery, dialogs, sticky bar, related rail. */
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- product */
  if (!customElements.get('dm-product')) {
    class DmProduct extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready) return;
        this.dataset.ready = '1';

        this.sectionId = this.dataset.section;
        this.productUrl = this.dataset.url;
        this.colorIndex = this.toIndex(this.dataset.colorIndex);
        this.sizeIndex = this.toIndex(this.dataset.sizeIndex);
        this.form = document.getElementById(this.dataset.form);
        this.variants = [];
        try {
          this.variants = JSON.parse(this.querySelector('[data-dm-variants]').textContent);
        } catch (e) {
          this.variants = [];
        }

        this.selection = [];
        this.querySelectorAll('[data-dm-option]').forEach((input) => {
          const i = Number(input.dataset.dmOption);
          if (this.selection[i] === undefined) this.selection[i] = null;
          if (input.checked) this.selection[i] = input.value;
        });
        if (!this.selection.length) {
          // No variant picker block: fall back to the server-rendered variant.
          const idInput = this.querySelector('[data-dm-variant-input]');
          const v = idInput && this.variants.find((x) => String(x.id) === idInput.value);
          if (v) this.selection = v.options.slice();
        }

        this.addEventListener('change', (e) => {
          if (e.target.matches('[data-dm-option]')) this.onOptionChange(e.target);
          if (e.target.matches('[name="pref"]')) this.hideFitResult();
        });
        this.addEventListener('click', (e) => this.onClick(e));
        if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));

        this.setupFitForm();
        this.setupSticky();
        this.refreshAvailability();
        this.updateBuyState();
      }

      toIndex(v) {
        return v === undefined || v === '' ? null : Number(v);
      }

      /* ---- variant helpers */
      hasSize() {
        return this.sizeIndex !== null && this.selection.length > 0;
      }

      sizeChosen() {
        return !this.hasSize() || !!this.selection[this.sizeIndex];
      }

      matches(variant, sel) {
        return sel.every((val, i) => val === null || val === undefined || variant.options[i] === val);
      }

      /* Variant used for display: exact match when complete, else the first (available) one for the chosen options. */
      currentVariant() {
        const sel = this.selection;
        if (this.sizeChosen()) {
          return this.variants.find((v) => v.options.every((o, i) => o === sel[i])) || null;
        }
        const pool = this.variants.filter((v) => this.matches(v, sel));
        return pool.find((v) => v.available) || pool[0] || null;
      }

      /* ---- events */
      onOptionChange(input) {
        const i = Number(input.dataset.dmOption);
        this.selection[i] = input.value;
        const label = this.querySelector(`[data-dm-option-label="${i}"]`);
        if (label) label.textContent = input.value;
        if (i === this.sizeIndex) this.hideSizeError();

        this.refreshAvailability();
        const variant = this.currentVariant();
        this.renderPrice(variant);
        this.updateBuyState();
        this.updateUrl(variant);

        if (i !== this.sizeIndex && variant) this.renderSection(variant.id);
      }

      onClick(e) {
        const thumb = e.target.closest('[data-dm-thumb]');
        if (thumb) return this.showThumb(thumb);

        const zoom = e.target.closest('[data-dm-zoom]');
        if (zoom) return this.openZoom(zoom.dataset.zoomSrc, zoom.querySelector('img'));

        const opener = e.target.closest('[data-dm-open]');
        if (opener) return this.openDialog(opener.dataset.dmOpen, opener);

        if (e.target.closest('[data-dm-close]')) return e.target.closest('dialog').close();

        const unitBtn = e.target.closest('[data-dm-unit]');
        if (unitBtn) return this.setFitUnits(unitBtn.dataset.dmUnit);

        const mUnit = e.target.closest('[data-dm-measure-unit]');
        if (mUnit) return this.setMeasureUnit(mUnit.dataset.dmMeasureUnit);

        const useSize = e.target.closest('[data-dm-use-size]');
        if (useSize) return this.useSize(useSize.dataset.dmUseSize);

        if (e.target.closest('[data-dm-sticky-btn]')) return this.onStickyBuy();

        // click on the dialog backdrop closes it
        if (e.target.matches('dialog.dm-dialog')) e.target.close();
      }

      /* ---- availability / price / buy state */
      refreshAvailability() {
        this.querySelectorAll('[data-dm-option]').forEach((input) => {
          const i = Number(input.dataset.dmOption);
          const sel = this.selection.slice();
          sel[i] = input.value;
          // For other options than the one being tested, only constrain by the color choice
          // so a chosen size never greys out a whole color.
          const constraint = sel.map((val, j) => (j === i || j === this.colorIndex ? val : null));
          const available = this.variants.some((v) => v.available && this.matches(v, constraint));
          input.closest('label').classList.toggle('is-unavailable', !available);
          // only say "sold out" in the markup when it is true (text readers ignore CSS)
          const sold = input.closest('label').querySelector('.dm-size__sold');
          if (sold) sold.textContent = available ? '' : ' (sold out)';
        });
      }

      priceHTML(variant) {
        if (!variant) return '';
        let html = `<span class="dm-pdp__price-now">${variant.price}</span>`;
        if (variant.compare) html += `<s class="dm-pdp__price-was"><span class="visually-hidden">Regular price</span>${variant.compare}</s>`;
        return html;
      }

      renderPrice(variant) {
        if (!variant) return;
        const price = this.querySelector('[data-dm-price]');
        if (price) price.innerHTML = this.priceHTML(variant);
        const sticky = this.querySelector('[data-dm-sticky-price]');
        if (sticky) sticky.textContent = variant.price;
      }

      updateBuyState() {
        const variant = this.currentVariant();
        const chosen = this.sizeChosen();
        const atc = this.querySelector('[data-dm-atc]');
        const label = this.querySelector('[data-dm-atc-label]');
        const input = this.querySelector('[data-dm-variant-input]');
        const stickyBtn = this.querySelector('[data-dm-sticky-btn]');
        const anyAvailable = this.variants.some((v) => v.available);

        let soldOut = !anyAvailable;
        if (chosen && (!variant || !variant.available)) soldOut = true;

        if (input && variant && chosen) input.value = variant.id;
        if (atc && label) {
          atc.disabled = soldOut;
          label.textContent = soldOut ? (variant || !chosen ? 'Sold out' : 'Unavailable') : label.dataset.default;
        }
        if (stickyBtn) {
          stickyBtn.disabled = soldOut;
          stickyBtn.textContent = soldOut ? 'Sold out' : stickyBtn.dataset.default || 'Add to cart';
        }

        const sel = this.querySelector('[data-dm-sticky-sel]');
        if (sel) {
          const parts = [];
          if (this.colorIndex !== null && this.selection[this.colorIndex]) parts.push(this.selection[this.colorIndex]);
          if (this.sizeIndex !== null) parts.push(this.selection[this.sizeIndex] || 'Choose size');
          sel.textContent = parts.join(' · ');
        }
        const shown = this.querySelector('[data-dm-shown-in]');
        if (shown && this.colorIndex !== null && this.selection[this.colorIndex]) shown.textContent = this.selection[this.colorIndex];
      }

      updateUrl(variant) {
        if (!variant || !this.sizeChosen()) return;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      /* ---- Section Rendering: swap media + price for the new color */
      renderSection(variantId) {
        if (this.abort) this.abort.abort();
        this.abort = new AbortController();
        const url = `${this.productUrl}?variant=${variantId}&section_id=${encodeURIComponent(this.sectionId)}`;
        fetch(url, { signal: this.abort.signal })
          .then((r) => r.text())
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const newMedia = doc.querySelector('[data-dm-media]');
            const media = this.querySelector('[data-dm-media]');
            if (newMedia && media) media.innerHTML = newMedia.innerHTML;
            // keep price in sync with the client-side selection (size may already be chosen)
            this.renderPrice(this.currentVariant());
            this.updateBuyState();
          })
          .catch((err) => {
            if (err.name !== 'AbortError') console.error(err);
          });
      }

      /* ---- gallery */
      showThumb(thumb) {
        const img = this.querySelector('.dm-pdp__stage-img');
        const stage = this.querySelector('[data-dm-zoom]');
        if (!img || !stage) return;
        img.srcset = thumb.dataset.srcset;
        img.src = thumb.dataset.src;
        img.alt = thumb.dataset.alt || '';
        stage.dataset.zoomSrc = thumb.dataset.zoomSrc;
        this.querySelectorAll('[data-dm-thumb]').forEach((t) => {
          t.classList.toggle('is-active', t === thumb);
          if (t === thumb) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
      }

      openZoom(src, img) {
        const dialog = this.querySelector('[data-dm-dialog="zoom"]');
        const zoomImg = this.querySelector('[data-dm-zoom-img]');
        if (!dialog || !zoomImg) return;
        zoomImg.src = src;
        zoomImg.alt = img ? img.alt : '';
        this.showDialog(dialog);
      }

      /* ---- dialogs */
      openDialog(name, opener) {
        const dialog = this.querySelector(`[data-dm-dialog="${name}"]`);
        if (!dialog) return;
        const open = opener.closest('dialog');
        if (open && open !== dialog) open.close();
        this.showDialog(dialog, open ? this.lastOpener : opener);
      }

      showDialog(dialog, opener) {
        this.lastOpener = opener || document.activeElement;
        if (!dialog.dataset.bound) {
          dialog.dataset.bound = '1';
          dialog.addEventListener('close', () => {
            document.documentElement.classList.remove('dm-dialog-open');
            if (!this.querySelector('dialog[open]') && this.lastOpener && this.lastOpener.isConnected) {
              this.lastOpener.focus({ preventScroll: true });
            }
          });
        }
        if (!dialog.open) dialog.showModal();
        document.documentElement.classList.add('dm-dialog-open');
      }

      /* ---- size error / ATC */
      sizeFieldset() {
        return this.querySelector('[data-dm-size-fieldset]');
      }

      showSizeError() {
        const fieldset = this.sizeFieldset();
        const err = this.querySelector('[data-dm-size-error]');
        if (err) {
          err.textContent = 'Choose a size to add this tee to your cart.';
          err.hidden = false;
        }
        if (fieldset) {
          fieldset.classList.add('has-error');
          fieldset.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
          const first = fieldset.querySelector('input:not(:disabled)');
          (first || fieldset).focus({ preventScroll: true });
        }
      }

      hideSizeError() {
        const err = this.querySelector('[data-dm-size-error]');
        if (err) err.hidden = true;
        const fieldset = this.sizeFieldset();
        if (fieldset) fieldset.classList.remove('has-error');
      }

      onStickyBuy() {
        if (!this.sizeChosen()) return this.showSizeError();
        if (this.form) this.form.requestSubmit();
      }

      onSubmit(e) {
        e.preventDefault();
        if (!this.sizeChosen()) return this.showSizeError();

        const variant = this.currentVariant();
        const atc = this.querySelector('[data-dm-atc]');
        const label = this.querySelector('[data-dm-atc-label]');
        const errorEl = this.querySelector('[data-dm-cart-error]');
        const added = this.querySelector('[data-dm-added]');
        const status = this.querySelector('[data-dm-status]');
        if (!variant || !variant.available || !atc || atc.getAttribute('aria-disabled') === 'true') return;

        const cart = document.querySelector('cart-drawer');
        const formData = new FormData();
        formData.append('id', variant.id);
        formData.append('quantity', '1');
        if (cart && typeof cart.renderContents === 'function') {
          formData.append('sections', ['cart-drawer', 'cart-icon-bubble'].join(','));
          formData.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }

        atc.setAttribute('aria-disabled', 'true');
        atc.classList.add('is-loading');
        label.textContent = 'Adding…';
        if (errorEl) errorEl.hidden = true;

        const addUrl = `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
        fetch(addUrl, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body: formData,
        })
          .then((r) => r.json())
          .then((json) => {
            if (json.status) {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, { source: 'dm-product', productVariantId: variant.id, errors: json.errors || json.description, message: json.message });
              }
              if (errorEl) {
                errorEl.textContent = json.description || json.message || 'Something went wrong. Please try again.';
                errorEl.hidden = false;
              }
              label.textContent = label.dataset.default;
              return;
            }

            label.textContent = 'Added';
            if (status) status.textContent = 'Added to your cart.';
            if (added) added.hidden = false;

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dm-product', productVariantId: variant.id, cartData: json });
            }

            if (cart && typeof cart.renderContents === 'function') {
              cart.classList.remove('is-empty');
              cart.renderContents(json);
            } else {
              window.location = this.dataset.cartUrl || '/cart';
              return;
            }
            clearTimeout(this.addedTimer);
            this.addedTimer = setTimeout(() => {
              label.textContent = label.dataset.default;
            }, 2200);
          })
          .catch((err) => {
            console.error(err);
            if (errorEl) {
              errorEl.textContent = 'We couldn’t reach the cart. Check your connection and try again.';
              errorEl.hidden = false;
            }
            label.textContent = label.dataset.default;
          })
          .finally(() => {
            atc.removeAttribute('aria-disabled');
            atc.classList.remove('is-loading');
          });
      }

      /* ---- sticky bar (mobile) */
      setupSticky() {
        const bar = this.querySelector('[data-dm-sticky]');
        const anchor = this.querySelector('[data-dm-buy-anchor]');
        if (!bar || !anchor || !('IntersectionObserver' in window)) return;
        const footer = document.querySelector('.shopify-section-group-footer-group, footer');
        let pastAnchor = false;
        let footerVisible = false;
        const sync = () => {
          const show = pastAnchor && !footerVisible;
          bar.classList.toggle('is-visible', show);
          bar.setAttribute('aria-hidden', show ? 'false' : 'true');
          bar.inert = !show;
          document.documentElement.classList.toggle('dm-sticky-on', show);
        };
        // A plain scroll check, not IntersectionObserver: some in-app and embedded browsers never deliver
        // observer callbacks, which left the bar hidden for good.
        let ticking = false;
        const check = () => {
          ticking = false;
          pastAnchor = anchor.getBoundingClientRect().bottom < 0;
          footerVisible = !!footer && footer.getBoundingClientRect().top < window.innerHeight;
          sync();
        };
        const onScroll = () => {
          if (ticking) return;
          ticking = true;
          setTimeout(check, 50);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        check();
      }

      /* ---- Find my size */
      setupFitForm() {
        const form = this.querySelector('[data-dm-fit-form]');
        if (!form) return;
        this.fitUnits = 'imperial';
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.suggestSize(form);
        });
      }

      setFitUnits(units) {
        this.fitUnits = units;
        this.querySelectorAll('[data-dm-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.dmUnit === units)));
        const imp = this.querySelector('[data-dm-units-imperial]');
        const met = this.querySelector('[data-dm-units-metric]');
        if (imp) imp.hidden = units !== 'imperial';
        if (met) met.hidden = units !== 'metric';
        this.hideFitResult();
      }

      setMeasureUnit(unit) {
        this.querySelectorAll('[data-dm-measure-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.dmMeasureUnit === unit)));
        const table = this.querySelector('[data-dm-measure-table]');
        if (table) table.dataset.unit = unit;
      }

      hideFitResult() {
        const result = this.querySelector('[data-dm-fit-result]');
        if (result) result.hidden = true;
      }

      suggestSize(form) {
        const err = this.querySelector('[data-dm-fit-error]');
        const result = this.querySelector('[data-dm-fit-result]');
        const num = (name) => parseFloat(form.elements[name] && form.elements[name].value);
        let heightIn;
        let weightLb;
        if (this.fitUnits === 'metric') {
          heightIn = num('cm') / 2.54;
          weightLb = num('kg') * 2.2046;
        } else {
          heightIn = num('ft') * 12 + (num('in') || 0);
          weightLb = num('lb');
        }
        if (!(heightIn > 48 && heightIn < 90) || !(weightLb > 70 && weightLb < 460)) {
          err.textContent = 'Add your height and weight so we can suggest a size.';
          err.hidden = false;
          result.hidden = true;
          return;
        }
        err.hidden = true;

        // Unisex relaxed tee (Comfort Colors 1717). Weight bands in lb, upper bound inclusive.
        const order = ['S', 'M', 'L', 'XL', '2XL', '3XL'];
        const bands = [
          [0, 145],
          [145, 170],
          [170, 195],
          [195, 225],
          [225, 260],
          [260, 999],
        ];
        let idx = bands.findIndex(([lo, hi]) => weightLb > lo && weightLb <= hi);
        if (idx < 0) idx = order.length - 1;
        const [lo, hi] = bands[idx];
        const pos = hi === 999 ? 0.5 : (weightLb - lo) / (hi - lo);

        if (heightIn >= 74 && idx < 3) idx += 1; // tall frames want the extra length
        const pref = (form.querySelector('[name="pref"]:checked') || {}).value || 'relaxed';
        if (pref === 'closer' && pos < 0.5) idx -= 1;
        if (pref === 'oversized') idx += 1;

        let tooBig = false;
        if (idx > order.length - 1) {
          tooBig = idx > order.length;
          idx = order.length - 1;
        }
        idx = Math.max(0, idx);
        const suggested = order[idx];

        const sizes = Array.from(this.querySelectorAll(`[data-dm-option="${this.sizeIndex}"]`)).map((i) => i.value);
        const match = sizes.find((s) => s.toUpperCase() === suggested);
        let html = `<p class="dm-fit__eyebrow">Estimated starting size</p><p class="dm-fit__size">${suggested}</p>`;
        if (tooBig) html += '<p class="dm-fit__note">You may want more room than our largest size. Compare the measurements below with a tee you love.</p>';
        else if (pref === 'oversized') html += '<p class="dm-fit__note">One size up from your usual for that easy, oversized hang.</p>';
        else html += '<p class="dm-fit__note">Not sure? Check the chest width below against a tee that already fits you well.</p>';
        if (match) html += `<button type="button" class="dm-btn" data-dm-use-size="${match}">Use size ${match}</button>`;
        else html += '<p class="dm-fit__note">That size isn’t offered for this design. Compare measurements to pick the closest one.</p>';
        result.innerHTML = html;
        result.hidden = false;
      }

      useSize(size) {
        const input = Array.from(this.querySelectorAll(`[data-dm-option="${this.sizeIndex}"]`)).find((i) => i.value === size);
        const dialog = this.querySelector('[data-dm-dialog="fit"]');
        if (dialog && dialog.open) dialog.close();
        if (!input) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.focus({ preventScroll: true });
      }
    }
    customElements.define('dm-product', DmProduct);
  }

  /* ------------------------------------------------------- related rail */
  if (!customElements.get('dm-related-tees')) {
    class DmRelatedTees extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready) return;
        this.dataset.ready = '1';
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-dm-rail-dir]');
          if (!btn) return;
          const track = this.querySelector('[data-dm-rail]');
          if (!track) return;
          const dir = Number(btn.dataset.dmRailDir);
          track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: reduceMotion() ? 'auto' : 'smooth' });
        });
        if (this.querySelector('[data-dm-rail]')) return; // already rendered (editor / cached)
        const load = () => {
          fetch(this.dataset.url)
            .then((r) => r.text())
            .then((text) => {
              const doc = new DOMParser().parseFromString(text, 'text/html');
              const fresh = doc.querySelector('dm-related-tees');
              if (fresh && fresh.querySelector('[data-dm-rail]')) {
                this.innerHTML = fresh.innerHTML;
              } else {
                const section = this.closest('.shopify-section');
                (section || this).hidden = true;
              }
            })
            .catch(() => {
              const section = this.closest('.shopify-section');
              (section || this).hidden = true;
            });
        };
        if (!('IntersectionObserver' in window)) return load();
        const io = new IntersectionObserver(
          (entries, obs) => {
            if (!entries[0].isIntersecting) return;
            obs.disconnect();
            load();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        io.observe(this);
      }
    }
    customElements.define('dm-related-tees', DmRelatedTees);
  }
})();
